import React, { useState, useEffect } from "react";
import { Show } from "@refinedev/mui";
import {
  Box,
  Button,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import { useCustom, useShow, useUpdate } from "@refinedev/core";
import { primary } from "style/color";
import { useNavigate, useParams } from "react-router-dom";
import { ICharity } from "interfaces/charity";
import { IRecommendedCharity } from "interfaces/recommended-charity";

export const EditRecommendedCharities: React.FC = () => {
  const navigate = useNavigate();
  const { id, ein } = useParams();
  const {
    data: charityData,
    isLoading: isQueryLoading,
  } = useCustom<ICharity>({
    url: `charities/${ein}`,
    method: "get",
    config: {
      query: {
        recommended: true,
      },
    },
  });
  const { queryResult } = useShow<IRecommendedCharity>({
    resource: "recommended-charity",
    id: id,
  });
  const { mutate, isLoading } = useUpdate();
  const record = charityData?.data;
  const { data } = queryResult;
  const recommendedCharity = data?.data;
  // State for form fields
  const [formData, setFormData] = useState({
    ein: record?.ein || "",
    name: "",
    organizationUrl: "",
    cause: "",
    mission: "",
    street: "",
    street2: "",
    city: "",
    state: "",
    zip: "",
    country: "",
    contactName: "",
    contactPhone: "",
    contactEmail: "",
    additionalInformation: "",
  });

  // Update form data when record is loaded
  useEffect(() => {
    if (record) {
      setFormData({
        ein: record.ein || "",
        name: record.name || "",
        organizationUrl: record.organizationUrl || "",
        cause: record.cause || "",
        mission: record.mission || "",
        street: record.street || "",
        street2: record.street2 || "",
        city: record.city || "",
        state: record.state || "",
        zip: record.zip || "",
        country: record.country || "",
        contactName: recommendedCharity?.contactName || "",
        contactPhone: recommendedCharity?.contactPhone || "",
        contactEmail: recommendedCharity?.contactEmail || "",
        additionalInformation: recommendedCharity?.additionalInformation || "",
      });
    }
  }, [record, recommendedCharity]);

  // Handle form field changes
  const handleChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  // Handle form submission
  const handleSubmit = () => {
    mutate({
      resource: "recommended-charity",
      id: `${id}/update`,
      values: formData,
    }, { onSuccess: () => { navigate("/charities/recommended") } });
  };


  return (
    <Show
      isLoading={isQueryLoading}
      title={
        <Typography variant="h5" color={primary}>
          Edit Recommended charity
        </Typography>
      }
    >
      {!isQueryLoading && (
        <>
          <Grid container spacing={1}>
            <Grid item xs={12} md={6}>
              <TextField
                disabled
                value={record?.ein || ""}
                margin="normal"
                fullWidth
                InputLabelProps={{ shrink: true }}
                type="text"
                label="EIN"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                value={formData.name}
                onChange={handleChange("name")}
                margin="normal"
                fullWidth
                InputLabelProps={{ shrink: true }}
                type="text"
                label="Name"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                value={formData.organizationUrl}
                onChange={handleChange("organizationUrl")}
                margin="normal"
                fullWidth
                InputLabelProps={{ shrink: true }}
                type="text"
                label="Organization Url"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                value={formData.cause}
                onChange={handleChange("cause")}
                margin="normal"
                fullWidth
                InputLabelProps={{ shrink: true }}
                type="text"
                label="Cause"
              />
            </Grid>
            <Grid item xs={12} md={12}>
              <TextField
                multiline
                rows={2}
                value={formData.mission}
                onChange={handleChange("mission")}
                margin="normal"
                fullWidth
                InputLabelProps={{ shrink: true }}
                type="text"
                label="Mission"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                value={formData.street}
                onChange={handleChange("street")}
                margin="normal"
                fullWidth
                InputLabelProps={{ shrink: true }}
                type="text"
                label="Street"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                value={formData.street2}
                onChange={handleChange("street2")}
                margin="normal"
                fullWidth
                InputLabelProps={{ shrink: true }}
                type="text"
                label="Street 2"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                value={formData.city}
                onChange={handleChange("city")}
                margin="normal"
                fullWidth
                InputLabelProps={{ shrink: true }}
                type="text"
                label="City"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                value={formData.state}
                onChange={handleChange("state")}
                margin="normal"
                fullWidth
                InputLabelProps={{ shrink: true }}
                type="text"
                label="State"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                value={formData.zip}
                onChange={handleChange("zip")}
                margin="normal"
                fullWidth
                InputLabelProps={{ shrink: true }}
                type="text"
                label="Zip"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                value={formData.country}
                onChange={handleChange("country")}
                margin="normal"
                fullWidth
                InputLabelProps={{ shrink: true }}
                type="text"
                label="Country"
              />
            </Grid>

            {/* Contact Information Section */}
            <Grid item xs={12} md={12}>
              <Typography variant="h6" sx={{ marginTop: 2, marginBottom: 1 }}>
                Contact Information
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                value={formData.contactName}
                onChange={handleChange("contactName")}
                margin="normal"
                fullWidth
                InputLabelProps={{ shrink: true }}
                type="text"
                label="Contact Name"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                value={formData.contactPhone}
                onChange={handleChange("contactPhone")}
                margin="normal"
                fullWidth
                InputLabelProps={{ shrink: true }}
                type="text"
                label="Contact Phone"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                value={formData.contactEmail}
                onChange={handleChange("contactEmail")}
                margin="normal"
                fullWidth
                InputLabelProps={{ shrink: true }}
                type="email"
                label="Contact Email"
              />
            </Grid>
            <Grid item xs={12} md={12}>
              <TextField
                multiline
                rows={3}
                value={formData.additionalInformation}
                onChange={handleChange("additionalInformation")}
                margin="normal"
                fullWidth
                InputLabelProps={{ shrink: true }}
                type="text"
                label="Additional Information"
              />
            </Grid>

            <Grid item xs={12} md={12} textAlign={"end"}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "flex-end",
                  marginTop: "20px",
                }}
              >
                <Button
                  size="large"
                  variant="contained"
                  style={{ marginRight: "0.5rem" }}
                  color="primary"
                  onClick={handleSubmit}
                  disabled={isLoading}
                >
                  {isLoading ? "Saving..." : "Confirm"}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </>
      )}
    </Show>
  );
};
